import type { Role } from './useAuthSession'
import type { RouteApi } from './useRoute'
import { useEffect, useState } from 'react'
import { openSportSheet } from './SportSheet'
import { supabase } from './supabaseClient'
import { hasRole } from './useAuthSession'

interface Props {
  route: RouteApi
  email: string | null
  role: Role | null
  isDark: boolean
  onToggleTheme: () => void
}

type Tab = 'embed' | 'distribution' | 'trading' | 'affiliate'

function formatClock(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

// Two-tier nav from the menus design handoff:
//   1) DesktopTopBar — always-dark utility band (API status + clock | identity | theme toggle)
//   2) Main header right cluster — EMBED / DISTRIBUTION underline tabs + keycap SIGN OUT
//   3) Mobile (<1024px) — main header collapses to logo + hamburger; menu opens
//      a bottom sheet with primary nav + identity + theme toggle. Per the
//      handoff, sign-out and system status do NOT live on mobile.
export default function Header({ route, email, role, isDark, onToggleTheme }: Props) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])

  function go(e: React.MouseEvent, to: string) {
    e.preventDefault()
    route.navigate(to)
  }

  async function signOut() {
    await supabase.auth.signOut()
    route.navigate('/login', true)
  }

  // startsWith so deep links (/distribution/sports/nhra, /trading/nhra)
  // highlight their parent tab too.
  const lowerPath = route.path.toLowerCase()
  const activeTab: Tab = lowerPath.startsWith('/trading')
    ? 'trading'
    : lowerPath.startsWith('/affiliate')
      ? 'affiliate'
      : lowerPath.startsWith('/distribution') ? 'distribution' : 'embed'
  // External users are scoped to the embed page only. Hide the
  // DISTRIBUTION nav link so they don't even see the option. TRADING is
  // desk-internal (Slack channels, contacts) — trading tier and above.
  // AFFILIATE is incubating — owner only, invisible to everyone else.
  const canSeeDistribution = hasRole(role, 'user')
  const canSeeTrading = hasRole(role, 'trading')
  const canSeeAffiliate = hasRole(role, 'affiliate')

  function changeTab(tab: Tab) {
    if (tab === 'trading') {
      route.navigate('/trading')
      return
    }
    if (tab === 'affiliate') {
      route.navigate('/affiliate')
      return
    }
    route.navigate(tab === 'distribution' ? '/distribution' : '/')
    // EMBED always lands on the embed home (overview) — navigate() no-ops
    // when already on '/', so App listens for this event and resets its
    // selection. Fresh mounts default to overview on their own.
    if (tab === 'embed')
      window.dispatchEvent(new Event('odds-portal:embed-home'))
  }

  return (
    <>
      {/* TOP UTILITY BAR — desktop only (always dark) */}
      <div className="b-topbar b-desktop-only">
        <div className="b-topbar-l">
          <span className="b-dot b-dot-on" />
          <span className="b-mono">API ONLINE</span>
          <span className="b-topbar-sep" aria-hidden="true">/</span>
          <span className="b-mono-dim">{formatClock(now)}</span>
        </div>
        <div className="b-topbar-r">
          {email && (
            <span className="b-topbar-identity">
              <span className="b-topbar-email">{email}</span>
              {role && (
                <>
                  <span className="b-topbar-role-sep" aria-hidden="true">/</span>
                  <span className="b-topbar-role" title="Access level">{role}</span>
                </>
              )}
            </span>
          )}
        </div>
        <div className="b-topbar-far">
          <button
            type="button"
            className="b-topbar-link b-topbar-toggle"
            onClick={onToggleTheme}
            aria-label="Toggle dark mode"
            title={isDark ? 'Switch to light' : 'Switch to dark'}
          >
            <span aria-hidden="true">{isDark ? '☀' : '☾'}</span>
            <span>{isDark ? 'LIGHT' : 'DARK'}</span>
          </button>
        </div>
      </div>

      {/* MAIN HEADER */}
      <header className="b-header">
        <div className="b-header-l">
          <a href="/" onClick={e => go(e, '/')} className="b-logomark ad-nav-logo">
            <img src="/asd-logo.png" alt="AltSports Data" />
          </a>
        </div>

        {/* Desktop center: page tabs, absolutely centered between logo and
            sign-out */}
        <div className="b-header-c b-desktop-only">
          <nav className="b-pagenav" aria-label="Primary">
            <button
              type="button"
              onClick={() => changeTab('embed')}
              className={`b-pagetab${activeTab === 'embed' ? ' is-active' : ''}`}
              aria-current={activeTab === 'embed' ? 'page' : undefined}
            >
              EMBED
            </button>
            {canSeeDistribution && (
              <button
                type="button"
                onClick={() => changeTab('distribution')}
                className={`b-pagetab${activeTab === 'distribution' ? ' is-active' : ''}`}
                aria-current={activeTab === 'distribution' ? 'page' : undefined}
              >
                DISTRIBUTION
              </button>
            )}
            {canSeeTrading && (
              <button
                type="button"
                onClick={() => changeTab('trading')}
                className={`b-pagetab${activeTab === 'trading' ? ' is-active' : ''}`}
                aria-current={activeTab === 'trading' ? 'page' : undefined}
              >
                TRADING
              </button>
            )}
            {canSeeAffiliate && (
              <button
                type="button"
                onClick={() => changeTab('affiliate')}
                className={`b-pagetab${activeTab === 'affiliate' ? ' is-active' : ''}`}
                aria-current={activeTab === 'affiliate' ? 'page' : undefined}
              >
                AFFILIATE
              </button>
            )}
          </nav>
        </div>

        {/* Desktop right cluster: keycap sign-out */}
        <div className="b-header-r b-desktop-only">
          <button
            type="button"
            className="b-signout"
            aria-label="Sign out"
            onClick={signOut}
          >
            <span className="b-signout-kbd">SIGN OUT</span>
          </button>
        </div>

        {/* Mobile right cluster: hamburger only */}
        <div className="b-header-r b-mobile-only">
          <button
            type="button"
            className="bm-menu-btn"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
          >
            <span className="bm-menu-bar" />
            <span className="bm-menu-bar" />
            <span className="bm-menu-bar" />
          </button>
        </div>
      </header>

      {/* MOBILE MENU SHEET */}
      {menuOpen && (
        <div
          className="bm-sheet-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget)
              setMenuOpen(false)
          }}
          role="presentation"
        >
          <div
            className="bm-sheet bm-menu-sheet"
            role="dialog"
            aria-label="Menu"
          >
            <div className="bm-sheet-head">
              <span className="b-mono">MENU</span>
              <button
                type="button"
                className="bm-sheet-close"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
              >
                {/*
                  Pixel-art X — 5×5 grid, 9 cells lit. shapeRendering="crispEdges"
                  is non-negotiable; without it the rasterizer drops fractional
                  pixels and the squares look uneven. Keep verbatim.
                */}
                <svg
                  viewBox="0 0 5 5"
                  width="15"
                  height="15"
                  aria-hidden="true"
                  shapeRendering="crispEdges"
                >
                  <rect x="0" y="0" width="1" height="1" />
                  <rect x="1" y="1" width="1" height="1" />
                  <rect x="2" y="2" width="1" height="1" />
                  <rect x="3" y="3" width="1" height="1" />
                  <rect x="4" y="4" width="1" height="1" />
                  <rect x="4" y="0" width="1" height="1" />
                  <rect x="3" y="1" width="1" height="1" />
                  <rect x="1" y="3" width="1" height="1" />
                  <rect x="0" y="4" width="1" height="1" />
                </svg>
              </button>
            </div>

            <div className="bm-menu-body">
              <nav className="bm-menu-nav" aria-label="Primary">
                <button
                  type="button"
                  onClick={() => {
                    changeTab('embed')
                    setMenuOpen(false)
                  }}
                  className={`bm-menu-navrow${activeTab === 'embed' ? ' is-active' : ''}`}
                >
                  <span className="bm-menu-navmark" aria-hidden="true">▸</span>
                  <span className="bm-menu-navlabel">EMBED</span>
                  <span className="b-mono-dim b-tiny">configure widget</span>
                </button>
                {canSeeDistribution && (
                  <button
                    type="button"
                    onClick={() => {
                      changeTab('distribution')
                      setMenuOpen(false)
                    }}
                    className={`bm-menu-navrow${activeTab === 'distribution' ? ' is-active' : ''}`}
                  >
                    <span className="bm-menu-navmark" aria-hidden="true">▸</span>
                    <span className="bm-menu-navlabel">DISTRIBUTION</span>
                    <span className="b-mono-dim b-tiny">channels &amp; usage</span>
                  </button>
                )}
                {canSeeTrading && (
                  <button
                    type="button"
                    onClick={() => {
                      changeTab('trading')
                      setMenuOpen(false)
                    }}
                    className={`bm-menu-navrow${activeTab === 'trading' ? ' is-active' : ''}`}
                  >
                    <span className="bm-menu-navmark" aria-hidden="true">▸</span>
                    <span className="bm-menu-navlabel">TRADING</span>
                    <span className="b-mono-dim b-tiny">sport ops notes</span>
                  </button>
                )}
                {canSeeAffiliate && (
                  <button
                    type="button"
                    onClick={() => {
                      changeTab('affiliate')
                      setMenuOpen(false)
                    }}
                    className={`bm-menu-navrow${activeTab === 'affiliate' ? ' is-active' : ''}`}
                  >
                    <span className="bm-menu-navmark" aria-hidden="true">▸</span>
                    <span className="bm-menu-navlabel">AFFILIATE</span>
                    <span className="b-mono-dim b-tiny">offers &amp; links</span>
                  </button>
                )}
                {/* Opens the current page's sport sheet — each page app
                    registers its own list via useSportSheetOpener. */}
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    openSportSheet()
                  }}
                  className="bm-menu-navrow"
                >
                  <span className="bm-menu-navmark" aria-hidden="true">▸</span>
                  <span className="bm-menu-navlabel">SPORTS</span>
                  <span className="b-mono-dim b-tiny">switch sport</span>
                </button>
              </nav>

              <div className="bm-menu-row bm-menu-account">
                <span className="bm-menu-val bm-menu-identity">
                  {email && <span className="bm-menu-email">{email}</span>}
                  {email && role && <span className="bm-menu-role-sep" aria-hidden="true">/</span>}
                  {role && <span className="bm-menu-role">{role}</span>}
                </span>
                <button
                  type="button"
                  className="bm-menu-action"
                  onClick={onToggleTheme}
                  aria-label="Toggle dark mode"
                >
                  {isDark ? 'LIGHT' : 'DARK'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
